import React, { useState, useEffect, useMemo, useRef } from 'react';

const PRAYER_ORDER = ['shacharit', 'mincha', 'maariv'];
const PRAYER_LABEL = { shacharit: 'שחרית', mincha: 'מנחה', maariv: 'מעריב' };
const PRAYER_ACCENT = { shacharit: '#1e3a5f', mincha: '#b8860b', maariv: '#374151' };

const ZMAN_SHORT = {
  daySunset: 'השקיעה', dayNetz: 'הנץ', dayAlot: 'עלות השחר', dayChatzot: 'חצות',
  dayMinchaGedola: 'מנחה גדולה', dayMinchaKetana: 'מנחה קטנה', dayPlagHamincha: 'פלג המנחה',
  dayTzeit7083: 'צאת הכוכבים', dayTzeit85: 'צאת הכוכבים', dayTzeit42: 'צאת הכוכבים',
  dayTzeit50: 'צאת הכוכבים', dayTzeit72: 'צאת הכוכבים',
  daySofZmanShma: 'סוף זמן שמע', daySofZmanShmaMGA: 'סוף זמן שמע',
  daySofZmanTfilla: 'סוף זמן תפילה', daySofZmanTfillaMGA: 'סוף זמן תפילה',
};
const DAY_NAMES = { 0: 'א׳', 1: 'ב׳', 2: 'ג׳', 3: 'ד׳', 4: 'ה׳', 5: 'ו׳', roshChodesh: 'ר״ח' };

const toMinutes = (t) => {
  if (!t || t === '--:--') return -1;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};
const getHebrewDay = () => {
  try { return parseInt(new Intl.DateTimeFormat('he-u-ca-hebrew', { day: 'numeric' }).format(new Date())); }
  catch { return 0; }
};
const isOverrideActive = (days, dow, isRoshChodesh) =>
  (days || []).includes(dow) || (isRoshChodesh && (days || []).includes('roshChodesh'));
const formatTimeRule = (ct) => {
  if (ct.type === 'fixed') return 'שעה קבועה';
  if (ct.type === 'relative') {
    const label = ZMAN_SHORT[ct.baseTime] || ct.baseTime;
    const offset = parseInt(ct.offset) || 0;
    if (offset === 0) return label;
    const abs = Math.abs(offset);
    return offset < 0 ? `${abs} דק׳ לפני ${label}` : `${abs} דק׳ אחרי ${label}`;
  }
  return null;
};

/* ── Auto-scroll hook ── */
const useAutoScroll = (ref, enabled) => {
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;

    let intervalId = null;

    const startScroll = () => {
      if (el.scrollHeight <= el.clientHeight + 2) return false;
      intervalId = setInterval(() => {
        if (el.scrollHeight <= el.clientHeight + 2) return;
        el.scrollTop += 1;
        const half = Math.floor(el.scrollHeight / 2);
        if (el.scrollTop >= half) el.scrollTop = 0;
      }, 50);
      return true;
    };

    if (!startScroll()) {
      const raf = requestAnimationFrame(startScroll);
      return () => { cancelAnimationFrame(raf); if (intervalId) clearInterval(intervalId); };
    }

    return () => { if (intervalId) clearInterval(intervalId); };
  }, [ref, enabled]);
};

/* ── Panel items (Modern/רשימה style) ── */
const PanelItems = ({ items, big, recurringDisplay, showRuleMode, dow, isRoshChodesh, highlightNextMinyan = false, nextItemIdx = -1 }) => (
  <div style={{ display: 'flex', flexDirection: 'column' }}>
    {items.map((ct, idx) => {
      const label = ct.label || PRAYER_LABEL[ct.prayer] || '';
      const isRecurring = ct.type === 'recurring';
      const activeOverride = ct._activeOverride;
      const isLast = idx === items.length - 1;
      const rule = !isRecurring ? formatTimeRule(ct) : null;
      const mainTime = activeOverride
        ? activeOverride.resolvedTime
        : isRecurring ? (ct.resolvedSlots?.[0] || null) : ct.resolvedTime;
      const isNext = highlightNextMinyan && idx === nextItemIdx;

      return (
        <div key={ct.id || idx} style={{
          paddingBottom: isNext ? '8px' : (isLast ? 0 : big ? '12px' : '6px'),
          marginBottom: isLast ? 0 : big ? '12px' : '6px',
          borderBottom: isNext ? 'none' : (isLast ? 'none' : '1px solid #f1f5f9'),
          ...(isNext ? {
            padding: '8px',
            backgroundColor: '#eff6ff',
            border: '2px solid #1e3a5f',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(30,58,95,0.12)',
            position: 'relative',
          } : {}),
        }}>
          {isNext && big && (
            <span style={{
              display: 'inline-block', marginBottom: '6px',
              fontSize: '11px', fontWeight: 700, color: '#fff',
              backgroundColor: '#1e3a5f', borderRadius: '10px', padding: '2px 10px',
            }}>המניין הבא</span>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span style={{ fontSize: big ? '19px' : '14px', fontWeight: 700, color: '#1e3a5f' }}>{label}</span>
              {(big && !showRuleMode && rule || ct.daysLabel) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 4px', alignItems: 'baseline' }}>
                  {big && !showRuleMode && rule && (
                    <span style={{ whiteSpace: 'nowrap', fontSize: big ? '12px' : '10px', color: '#64748b', fontWeight: 500, lineHeight: 1 }}>
                      {rule}{ct.daysLabel ? ' ·' : ''}
                    </span>
                  )}
                  {ct.daysLabel && (
                    <span style={{ whiteSpace: 'nowrap', fontSize: big ? '12px' : '10px', color: '#64748b', fontWeight: 500, lineHeight: 1 }}>
                      {ct.daysLabel}
                    </span>
                  )}
                </div>
              )}
            </div>
            {isRecurring ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <span style={{
                  fontSize: big ? '13px' : '11px', color: '#166534', fontWeight: 600,
                  backgroundColor: '#dcfce7', borderRadius: '20px', padding: '2px 10px', flexShrink: 0,
                }}>כל {ct.interval} דק׳</span>
                {ct.resolvedSlots?.length > 0 && (
                  <span style={{
                    fontSize: big ? '16px' : '14px', fontWeight: 600, color: '#475569',
                    fontVariantNumeric: 'tabular-nums', direction: 'ltr', flexShrink: 0,
                  }}>{ct.resolvedSlots[0]} — {ct.resolvedSlots[ct.resolvedSlots.length - 1]}</span>
                )}
              </div>
            ) : (
              showRuleMode && ct.type === 'relative' ? (
                <span style={{ fontSize: big ? '14px' : '12px', fontWeight: 600, color: '#475569' }}>{rule}</span>
              ) : (
                <span style={{
                  fontSize: big ? '23px' : '17px', fontWeight: 700,
                  color: activeOverride ? '#166534' : '#1e3a5f',
                  fontVariantNumeric: 'tabular-nums', direction: 'ltr',
                }}>{mainTime || '—'}</span>
              )
            )}
          </div>

          {big && isRecurring && recurringDisplay === 'full' && ct.resolvedSlots?.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(54px, 1fr))', gap: '4px 6px', marginTop: '8px', direction: 'ltr' }}>
              {ct.resolvedSlots.map((slot, i) => (
                <span key={i} style={{ fontSize: '13px', fontWeight: 600, color: '#475569', fontVariantNumeric: 'tabular-nums', textAlign: 'center', backgroundColor: '#f8fafc', borderRadius: '4px', padding: '2px 0' }}>{slot}</span>
              ))}
            </div>
          )}

          {(ct.resolvedOverrides || ct.dayOverrides || []).map((ov, oi) => {
            const active = isOverrideActive(ov.days, dow, isRoshChodesh);
            if (!big && !active) return null;
            const dayLabels = (ov.days || []).map(d => DAY_NAMES[d]).filter(Boolean).join(' ');
            const time = ov.resolvedTime || ov.time || '--:--';
            return (
              <div key={oi} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginTop: '6px', paddingRight: '12px', paddingLeft: '8px',
                paddingTop: active ? '5px' : 0, paddingBottom: active ? '5px' : 0,
                borderRadius: active ? '8px' : 0,
                backgroundColor: active ? '#f0fdf4' : 'transparent',
                border: active ? '1px solid #bbf7d0' : 'none',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {active && <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#16a34a', flexShrink: 0, display: 'inline-block' }} />}
                  <span style={{ fontSize: big ? '14px' : '12px', color: active ? '#15803d' : '#64748b', fontWeight: active ? 600 : 500 }}>{dayLabels}</span>
                </div>
                <span style={{ fontSize: big ? '16px' : '14px', fontWeight: 700, color: active ? '#15803d' : '#475569', fontVariantNumeric: 'tabular-nums', direction: 'ltr' }}>{time}</span>
              </div>
            );
          })}
        </div>
      );
    })}
  </div>
);

/* ── Small panel with auto-scroll ── */
const SmallPanel = ({ panelKey, accent, items, recurringDisplay, showRuleMode, dow, isRoshChodesh }) => {
  const scrollRef = useRef(null);
  useAutoScroll(scrollRef, true);

  const content = (
    <div style={{ padding: '4px 20px 8px' }}>
      <PanelItems items={items} big={false} recurringDisplay={recurringDisplay} showRuleMode={showRuleMode} dow={dow} isRoshChodesh={isRoshChodesh} />
    </div>
  );

  return (
    <div style={{ borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ padding: '0 20px', height: '24px', borderRight: `3px solid ${accent}`, display: 'flex', alignItems: 'center' }}>
        <span style={{ fontSize: '11px', fontWeight: 700, color: accent, letterSpacing: '0.05em' }}>{PRAYER_LABEL[panelKey]}</span>
      </div>
      <div ref={scrollRef} style={{ maxHeight: '120px', overflow: 'hidden' }}>
        {content}
        <div aria-hidden="true">{content}</div>
      </div>
    </div>
  );
};

/* ── Main ── */
const Triptych = ({ processedCustomTimes, loading, error, title = 'תפילות יום חול', showTitle = true, recurringDisplay = 'compact', showRuleMode = false, highlightNextMinyan = false }) => {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(t); }, []);

  const dow = now.getDay();
  const hebrewDay = getHebrewDay();
  const isRoshChodesh = hebrewDay === 1 || hebrewDay === 30;
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const adjNowMin = nowMin < 180 ? nowMin + 1440 : nowMin;

  const { groups, currentKey } = useMemo(() => {
    if (!processedCustomTimes?.length) return { groups: {}, currentKey: 'shacharit' };
    const grp = { shacharit: [], mincha: [], maariv: [] };
    for (const ct of processedCustomTimes) {
      const key = ct.prayer;
      if (!grp[key]) continue;
      const activeOverride = (ct.resolvedOverrides || []).find(ov => isOverrideActive(ov.days, dow, isRoshChodesh));
      let times = [];
      if (ct.type === 'recurring') {
        times = activeOverride ? [toMinutes(activeOverride.resolvedTime)] : (ct.resolvedSlots || []).map(toMinutes).filter(m => m >= 0);
      } else {
        const m = toMinutes(activeOverride ? activeOverride.resolvedTime : ct.resolvedTime);
        if (m >= 0) times = [m];
      }
      grp[key].push({ ...ct, _times: times, _activeOverride: activeOverride });
    }
    const ranges = {};
    for (const k of PRAYER_ORDER) { const t = grp[k].flatMap(e => e._times); if (t.length) ranges[k] = { min: Math.min(...t), max: Math.max(...t) }; }
    const activeKeys = PRAYER_ORDER.filter(k => ranges[k]);
    let current = activeKeys[activeKeys.length - 1] || 'maariv';
    for (let i = 0; i < activeKeys.length; i++) {
      if (adjNowMin <= ranges[activeKeys[i]].max + 30) { current = activeKeys[i]; break; }
    }
    if (activeKeys.length === 1) current = activeKeys[0];
    if (!activeKeys.length) current = adjNowMin < 720 ? 'shacharit' : adjNowMin < 1020 ? 'mincha' : 'maariv';
    return { groups: grp, currentKey: current };
  }, [processedCustomTimes, dow, isRoshChodesh, adjNowMin]);

  const nextItemIdx = useMemo(() => {
    if (!highlightNextMinyan) return -1;
    const items = groups[currentKey] || [];
    let bestIdx = -1, bestMin = Infinity;
    for (let i = 0; i < items.length; i++) {
      const ct = items[i];
      if (ct.isActiveToday === false) continue;
      for (const t of (ct._times || [])) {
        if (t >= adjNowMin && t < bestMin) { bestMin = t; bestIdx = i; }
      }
    }
    return bestIdx;
  }, [highlightNextMinyan, groups, currentKey, adjNowMin]);

  if (loading) return <div style={styles.state}>טוען זמני תפילה...</div>;
  if (error) return <div style={{ ...styles.state, color: '#D32F2F' }}>{error}</div>;
  if (!processedCustomTimes?.length) return <div style={styles.state}>אין זמני תפילה</div>;

  const activePanels = PRAYER_ORDER.filter(k => groups[k]?.length > 0);

  return (
    <div style={{ height: '100%', width: '100%', direction: 'rtl', padding: '20px', boxSizing: 'border-box', overflowY: 'auto', fontFamily: 'inherit' }}>
      {showTitle && (
        <div style={{ paddingBottom: '16px', marginBottom: '16px', borderBottom: '2px solid #e5ddd3' }}>
          <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#1e3a5f' }}>{title}</h2>
        </div>
      )}

      {/* Non-current panels BEFORE current */}
      {activePanels.filter(k => k !== currentKey && PRAYER_ORDER.indexOf(k) < PRAYER_ORDER.indexOf(currentKey)).map(k => (
        <SmallPanel key={k} panelKey={k} accent={PRAYER_ACCENT[k]} items={groups[k]}
          recurringDisplay={recurringDisplay} showRuleMode={showRuleMode} dow={dow} isRoshChodesh={isRoshChodesh} />
      ))}

      {/* Current panel — full size */}
      {groups[currentKey]?.length > 0 && (
        <div style={{ borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ padding: '0 0 4px', borderRight: `3px solid ${PRAYER_ACCENT[currentKey]}`, paddingRight: '8px', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: PRAYER_ACCENT[currentKey], letterSpacing: '0.05em' }}>{PRAYER_LABEL[currentKey]}</span>
          </div>
          <PanelItems items={groups[currentKey]} big={true} recurringDisplay={recurringDisplay} showRuleMode={showRuleMode} dow={dow} isRoshChodesh={isRoshChodesh} highlightNextMinyan={highlightNextMinyan} nextItemIdx={nextItemIdx} />
        </div>
      )}

      {/* Non-current panels AFTER current */}
      {activePanels.filter(k => k !== currentKey && PRAYER_ORDER.indexOf(k) > PRAYER_ORDER.indexOf(currentKey)).map(k => (
        <SmallPanel key={k} panelKey={k} accent={PRAYER_ACCENT[k]} items={groups[k]}
          recurringDisplay={recurringDisplay} showRuleMode={showRuleMode} dow={dow} isRoshChodesh={isRoshChodesh} />
      ))}
    </div>
  );
};

const styles = { state: { width: '100%', padding: '20px', textAlign: 'center', direction: 'rtl', fontSize: '18px', color: '#64748b' } };

export default Triptych;
